import {
    useGetMemberValidationsMutation,
    useToggleArchiveMemberValidationMutation,
    useGetArchivedMemberValidationsMutation,
} from '@services/member/member-validation/member-validation.endpoints';
import { PaginationMetadata } from '@services/providers/cosem.types';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useSelector } from 'react-redux';
import { getUser } from '@features/auth/auth.slice';
import { MemberValidationTable } from '@services/member/member-validation/member-validation.types';

export const useListPageVM = () => {
    const MySwal = withReactContent(Swal);

    const [totalMemberValidations, setTotalMemberValidations] = useState<number>(0);
    const [isArchived, setIsArchived] = useState<boolean>(false);
    const columnHelper = createColumnHelper<MemberValidationTable>();
    const user = useSelector(getUser);
    const navigate = useNavigate();
    const [needReload, setNeedReload] = useState<boolean>();

    const [toggleArchiveMemberValidation, { isSuccess: isToggleArchiveMemberValidationSuccess, isLoading: isToggleArchiveMemberValidationLoading }] =
        useToggleArchiveMemberValidationMutation();
    const mutationHook = useMemo(() => {
        return isArchived ? useGetArchivedMemberValidationsMutation : useGetMemberValidationsMutation;
    }, [isArchived]);

    useEffect(() => {
        setTotalMemberValidations(0);
    }, [isArchived]);

    useEffect(() => {
        if (isToggleArchiveMemberValidationSuccess) {
            setNeedReload((prev) => !prev);
        }
    }, [isToggleArchiveMemberValidationSuccess]);

    const handleMetadata = (metadata: PaginationMetadata) => {
        if (metadata && metadata.total !== undefined) {
            setTotalMemberValidations(metadata.total);
        }
    };

    const handleToggleArchiveMemberValidation = (id: number, archive: boolean) => {
        MySwal.fire({
            title: archive
                ? 'Êtes-vous sûr de vouloir archiver cette validation ?'
                : 'Êtes-vous sûr de vouloir désarchiver cette validation ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: archive ? 'Archiver' : 'Désarchiver',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                toggleArchiveMemberValidation({ id, archived: archive });
            }
        });
    };

    const handleToggleArchiveMemberValidations = (lines: MemberValidationTable[], archive: boolean) => {
        MySwal.fire({
            title: archive
                ? 'Êtes-vous sûr de vouloir archiver ces validations ?'
                : 'Êtes-vous sûr de vouloir désarchiver ces validations ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: archive ? 'Archiver' : 'Désarchiver',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                lines.forEach((line) => {
                    toggleArchiveMemberValidation({ id: line.id, archived: archive });
                });
            }
        });
    };

    return {
        handleToggleArchiveMemberValidation,
        handleToggleArchiveMemberValidations,
        handleMetadata,
        navigate,
        columnHelper,
        isArchived,
        mutationHook,
        setIsArchived,
        totalMemberValidations,
        isLoading: isToggleArchiveMemberValidationLoading,
        needReload,
        user
    };
};
